import { useEffect, useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  useCompanySearch,
  useCreateRequest,
  type CompanyOption,
  type RequestAction,
} from "../api";

/**
 * Propose a relationship change. Every path lands as a PENDING request that a
 * second user must approve — nothing is written to Cinch from here.
 *
 * Modes:
 * - add:    pick parent + child (either may be prefilled from context)
 * - remove: parent + child prefilled from the tree row
 * - move:   child + current parent prefilled; pick the destination parent
 */
export function RelationshipDialog({
  open,
  onOpenChange,
  mode,
  parent,
  child,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: RequestAction;
  parent?: CompanyOption | null;
  child?: CompanyOption | null;
}) {
  const create = useCreateRequest();
  const [parentPick, setParentPick] = useState<CompanyOption | null>(parent ?? null);
  const [childPick, setChildPick] = useState<CompanyOption | null>(child ?? null);
  const [newParentPick, setNewParentPick] = useState<CompanyOption | null>(null);
  const [reason, setReason] = useState("");

  // Re-seed when the dialog opens for a different row.
  useEffect(() => {
    if (open) {
      setParentPick(parent ?? null);
      setChildPick(child ?? null);
      setNewParentPick(null);
      setReason("");
    }
  }, [open, parent, child]);

  const title =
    mode === "add"
      ? "Propose new parent → child link"
      : mode === "remove"
        ? "Propose removing this link"
        : "Propose moving this child";

  const ready =
    !!parentPick &&
    !!childPick &&
    reason.trim().length > 0 &&
    (mode !== "move" || !!newParentPick);

  const submit = () => {
    if (!parentPick || !childPick) return;
    create.mutate(
      {
        action: mode,
        parent_company_id: parentPick.id,
        child_company_id: childPick.id,
        new_parent_company_id: mode === "move" ? newParentPick?.id : undefined,
        reason: reason.trim(),
      },
      {
        onSuccess: () => {
          toast.success("Change proposed — awaiting a second user's approval");
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            The change goes to the approvals queue; a second user must sign off
            before it's applied in Cinch. Every step is logged.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0 everywhere below: DialogContent is a grid, and grid items
            default to min-width:auto — one un-wrappable company/account name
            would otherwise blow the whole dialog wider than its max-w panel
            (text and inputs painting past the white background). */}
        <div className="flex min-w-0 flex-col gap-3">
          <CompanyField
            label="Parent company"
            value={parentPick}
            onChange={setParentPick}
            locked={mode !== "add" && !!parent}
          />
          <CompanyField
            label="Child company"
            value={childPick}
            onChange={setChildPick}
            locked={mode !== "add" && !!child}
          />
          {mode === "move" && (
            <CompanyField
              label="New parent company"
              value={newParentPick}
              onChange={setNewParentPick}
            />
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pc-reason">Why this change is right</Label>
            <Textarea
              id="pc-reason"
              placeholder="e.g. Acquired by the group in June — users and billing already moved over."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!ready || create.isPending} onClick={submit}>
            {create.isPending ? "Submitting…" : "Submit for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Async company picker: type ≥2 chars, results come from the Cinch search. */
function CompanyField({
  label,
  value,
  onChange,
  locked = false,
}: {
  label: string;
  value: CompanyOption | null;
  onChange: (v: CompanyOption | null) => void;
  locked?: boolean;
}) {
  const [query, setQuery] = useState("");
  const search = useCompanySearch(query);
  const showResults = !value && query.trim().length >= 2;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label>{label}</Label>
      {value ? (
        <div className="flex min-w-0 items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium">{value.name}</span>
            {value.account_name && (
              <span className="text-muted-foreground"> · {value.account_name}</span>
            )}
          </span>
          {!locked && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setQuery("");
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder="Search Cinch companies…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {showResults && (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-background shadow-md">
              {search.isLoading && (
                <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
              )}
              {search.data?.items.length === 0 && !search.isLoading && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No matches.</div>
              )}
              {(search.data?.items ?? []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onChange(c)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                    c.is_active === false && "opacity-60",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {c.account_name && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {c.account_name}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
